import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import api from "./api";

const AuthContext = createContext(null);

// Stabilization phase (Feb 2026) — production posture:
//
// 1. The token (`ren_token`) is the single source of truth. `user` is just
//    its in-memory hydration. Whenever the token disappears (logout, 401,
//    storage event from sibling tab) we drop `user` and surface a controlled
//    "Session expired" modal instead of letting the dashboard blank out.
// 2. We re-validate the token on `visibilitychange` (tab regains focus) and
//    `online` events with light debouncing so a sleeping laptop coming back
//    doesn't fire bursts of /auth/me.
// 3. We listen for `storage` events so logging out in one tab logs out every
//    other tab automatically.
// 4. `auth:session-expired` is dispatched by the axios interceptor on any
//    non-impersonation 401 — we consume it here to flip into the "needs
//    re-login" state.

const REVALIDATE_DEBOUNCE_MS = 4_000;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);
  const lastRevalidateRef = useRef(0);

  // ---- Initial bootstrap ---------------------------------------------------
  useEffect(() => {
    const token = localStorage.getItem("ren_token");
    if (!token) { setLoading(false); return; }
    api.get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => localStorage.removeItem("ren_token"))
      .finally(() => setLoading(false));
  }, []);

  // ---- Cross-tab logout sync + session-expired listener -------------------
  useEffect(() => {
    function onStorage(e) {
      // Sibling tab cleared the token → drop user here too. We avoid setting
      // `sessionExpired` because a tab that voluntarily logged out is not
      // really "expired" — just navigate the user back to login organically.
      if (e.key === "ren_token" && !e.newValue && user) {
        setUser(null);
      }
    }
    function onSessionExpired() {
      // Only surface the modal if we were actually authenticated. An
      // anonymous visitor triggering a 401 should never see this.
      setSessionExpired(true);
      setUser(null);
    }
    window.addEventListener("storage", onStorage);
    window.addEventListener("auth:session-expired", onSessionExpired);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("auth:session-expired", onSessionExpired);
    };
  }, [user]);

  // ---- Foreground revalidation --------------------------------------------
  // When a user backgrounds the tab (laptop lid closed, switched tabs) we
  // briefly verify the token is still valid when they come back. This catches
  // server-side invalidations (admin disable, password rotate, db migration)
  // BEFORE the user triggers a 5-request cascade by clicking around.
  const revalidate = useCallback(async () => {
    if (!localStorage.getItem("ren_token")) return;
    const now = Date.now();
    if (now - lastRevalidateRef.current < REVALIDATE_DEBOUNCE_MS) return;
    lastRevalidateRef.current = now;
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (_) {
      // The 401 case already broadcasts session-expired via the interceptor.
      // Anything else (network blip) — swallow silently.
    }
  }, []);

  useEffect(() => {
    function onVis() {
      if (document.visibilityState === "visible") revalidate();
    }
    function onOnline() { revalidate(); }
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("online", onOnline);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("online", onOnline);
    };
  }, [revalidate]);

  // ---- Public API ----------------------------------------------------------
  const login = async (email, password, portal = "customer") => {
    const { data } = await api.post("/auth/login", { email, password, portal });
    localStorage.setItem("ren_token", data.token);
    setUser(data.user);
    setSessionExpired(false);
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    localStorage.setItem("ren_token", data.token);
    setUser(data.user);
    return data;
  };

  const updateToken = (token, nextUser) => {
    if (token) localStorage.setItem("ren_token", token);
    if (nextUser) setUser(nextUser);
  };

  const logout = () => {
    // Stabilization phase (Feb 2026): tenant-isolation hardening.
    // We deliberately wipe the entire `ren_*` and `sa_*` localStorage
    // namespace on logout (not just the token) so that no tenant-scoped
    // state can ever bleed into the next user's session — particularly in
    // shared browsers. Impersonation backup + the chat session id are also
    // dropped because they're scoped to the outgoing session.
    try {
      const STICKY_PREFIXES = ["ren_", "sa_", "impersonation_", "rch_"];
      const toDrop = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && STICKY_PREFIXES.some((p) => k.startsWith(p))) toDrop.push(k);
      }
      toDrop.forEach((k) => localStorage.removeItem(k));
    } catch (_) { /* localStorage may be locked down */ }
    setUser(null);
    setSessionExpired(false);
  };

  const dismissSessionExpired = () => setSessionExpired(false);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        sessionExpired,
        login,
        register,
        logout,
        updateToken,
        revalidate,
        dismissSessionExpired,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
